import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { IndianRupee, Save, Trash } from 'lucide-react';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/**
 * Add Deduction dialog — records a per-month advance / deduction for a staff member.
 * Props:
 *   staff: payroll row OR raw staff object (uses .staff_id || .id and .staff_name || .name)
 *   selectedMonth: 'YYYY-MM'
 *   newDeduction: { amount, reason, date }
 *   setNewDeduction: setter
 *   onSubmit: (e) => Promise
 *   onClose: () => void
 *   onDeleteAdjustment: (adjId) => Promise
 */
const AddDeductionDialog = ({ staff, selectedMonth, newDeduction, setNewDeduction, onSubmit, onClose, onDeleteAdjustment }) => {
  const monthLabel = new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <Dialog open={!!staff} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="deduction-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-amber-600" />Add Deduction
          </DialogTitle>
          <DialogDescription>
            Record an advance / deduction for {staff?.staff_name || staff?.name} — {monthLabel}
          </DialogDescription>
        </DialogHeader>
        {staff && (
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Amount (₹) *</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={newDeduction.amount}
                  onChange={(e) => setNewDeduction({ ...newDeduction, amount: e.target.value })}
                  required className="h-11" data-testid="deduction-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={newDeduction.date}
                  onChange={(e) => setNewDeduction({ ...newDeduction, date: e.target.value })}
                  className="h-11"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Reason</Label>
                <Input value={newDeduction.reason}
                  onChange={(e) => setNewDeduction({ ...newDeduction, reason: e.target.value })}
                  placeholder="Advance" className="h-11" data-testid="deduction-reason"
                />
              </div>
            </div>

            {staff.adjustments?.length > 0 && (
              <div className="border rounded-lg p-3 bg-slate-50">
                <div className="text-xs text-slate-500 mb-2">Existing deductions this month</div>
                <div className="space-y-1 max-h-32 overflow-auto">
                  {staff.adjustments.map(a => (
                    <div key={a.id} className="flex items-center justify-between text-sm">
                      <div>
                        <span className="font-medium">{fmtINR(a.amount)}</span>
                        <span className="text-slate-500 ml-2">{a.reason} • {a.date}</span>
                      </div>
                      <Button
                        size="icon" variant="ghost" className="h-6 w-6 text-red-500"
                        onClick={() => onDeleteAdjustment(a.id)} type="button"
                      >
                        <Trash className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid="deduction-save">
                <Save className="h-4 w-4 mr-1" />Add Deduction
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Close</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddDeductionDialog;
